import React, { useState, useEffect } from "react";
import { Card, Tooltip } from "antd";
import { ApartmentOutlined } from "@ant-design/icons";
import {
  Button,
  Text,
  SpeakerPersonIcon,
  WhiteboardIcon,
  TrashCanIcon,
  InfoIcon,
  SettingsIcon,
} from "@fluentui/react-northstar";
import { SpinWheelIcon } from "../../../../Assets/icons/icons";
import { useTranslation } from "react-i18next";
import "./TrustedNetworkCard.scss";

const TrustedNetworkCard = ({
  schoolName,
  schoolLogo,
  teacherCount = 0,
  collectionsCount = 0,
  spinWheelsCount = 0,
  onEndNetwork,
  onOpenConfig,
  isEnding,
  contactNumber,
  schoolAddress,
  superAdminName,
  superAdminEmail,
  teachersCount,
  studentsCount,
  onInfoClick,
  onViewBranches,
}) => {
  const { t } = useTranslation();
  const translation = t("trustedNetwork").trustedNetworkCard;

  const [logo, setLogo] = useState(() => {
    if (!schoolLogo) return null;
    if (schoolLogo.includes("?")) return schoolLogo;

    const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN?.replace(/"/g, "");
    return sasToken ? schoolLogo + sasToken : schoolLogo;
  });

  const [hovered, setHovered] = useState(false);
  const [metricHovered, setMetricHovered] = useState(null);

  // Update logo when schoolLogo prop changes
  useEffect(() => {
    if (!schoolLogo) {
      setLogo(null);
      return;
    }
    if (schoolLogo.includes("?")) {
      setLogo(schoolLogo);
      return;
    }

    const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN?.replace(/"/g, "");
    setLogo(sasToken ? schoolLogo + sasToken : schoolLogo);
  }, [schoolLogo]);

  // Consistent icon box for all metrics
  const iconBoxStyle = {
    width: "clamp(18px, 2.5vw, 22px)",
    height: "clamp(18px, 2.5vw, 22px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const metrics = [
    {
      icon: (
        <div style={iconBoxStyle}>
          <SpeakerPersonIcon />
        </div>
      ),
      label: translation?.teachers || "Teachers",
      count: teachersCount || teacherCount,
      tooltip: translation?.teachersTooltip || `${translation?.teachers || "Teachers"}: ${
        teachersCount || teacherCount
      }`,
    },
    {
      icon: (
        <div style={iconBoxStyle}>
          <WhiteboardIcon />
        </div>
      ),
      label: translation?.collections || "Collections",
      count: collectionsCount,
      tooltip: translation?.collectionsTooltip || `${
        translation?.collections || "Collections"
      }: ${collectionsCount}`,
    },
    {
      icon: <div style={iconBoxStyle}>{SpinWheelIcon("#6264a7")}</div>,
      label: translation?.spinWheels || "Spinwheels",
      count: spinWheelsCount,
      tooltip: translation?.spinWheelsTooltip || `${translation?.spinWheels || "Spinwheels"}: ${spinWheelsCount}`,
    },
    {
      icon: (
        <div style={iconBoxStyle}>
          <ApartmentOutlined style={{ fontSize: "clamp(18px, 2.5vw, 22px)" }} />
        </div>
      ),
      label: translation?.branches || "Branches Details",
      count: "", // No count display for branches
      tooltip: translation?.viewBranchesTooltip || "Click to view branches",
      onClick: onViewBranches,
    },
  ];

  return (
    <Card
      hoverable
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`trusted-network-card ${hovered ? "hovered" : ""}`}
      bodyStyle={{
        padding: 0,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header Section */}
      <div className="header-section">
        <div className="background-pattern" />
        <div className="header-content">
          {/* Avatar */}
          <div className="avatar-container">
            <div className="avatar">
              {logo ? (
                <img
                  src={logo}
                  alt={`${schoolName} Logo`}
                  className="logo-image"
                  onError={() => setLogo(null)}
                  onLoad={() => console.log("Logo loaded:", logo)}
                />
              ) : (
                <div className="logo-fallback">
                  {schoolName?.charAt(0)?.toUpperCase()}
                </div>
              )}
            </div>
          </div>

          {/* School Info */}
          <div className="school-info">
            <div className="school-header">
              <div className="school-title">
                <Text
                  content={schoolName}
                  weight="bold"
                  title={schoolName}
                  className="school-name"
                />
                {superAdminEmail && (
                  <div className="school-email" title={superAdminEmail}>
                    {superAdminEmail}
                  </div>
                )}
              </div>
              {/* Info Button */}
              <Button
                icon={<InfoIcon outline />}
                content={translation?.details || "Details"}
                size="small"
                title={translation?.info || "Details"}
                onClick={() =>
                  onInfoClick({
                    schoolName,
                    contactNumber,
                    schoolAddress,
                    superAdminName,
                    superAdminEmail,
                    teachersCount,
                    studentsCount,
                  })
                }
                className="info-button"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="metrics-section">
        <div className="metrics-grid">
          {metrics.map((metric, index) => {
            const isBranches =
              metric.label === (translation?.branches || "Branches Details");

            const box = (
              <div
                className={`metric-box ${
                  metricHovered === index ? "hovered" : ""
                } ${isBranches ? "branches-metric" : ""}`}
                onMouseEnter={() => setMetricHovered(index)}
                onMouseLeave={() => setMetricHovered(null)}
                onClick={metric.onClick}
                style={{ cursor: metric.onClick ? "pointer" : "default" }}
              >
                <div className="metric-icon">{metric.icon}</div>
                <span
                  className={`metric-count ${
                    metric.count === 0 ? "zero-value" : ""
                  }`}
                >
                  {metric.count}
                </span>
                <div className="metric-label">{metric.label}</div>
              </div>
            );

            return (
              <div key={index} className="metric-wrapper">
                {isBranches ? (
                  <Tooltip title={metric.tooltip}>{box}</Tooltip>
                ) : (
                  box
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions Section */}
      <div className="actions-section">
        <Button
          size="small"
          icon={<TrashCanIcon />}
          content={translation?.end || "End Network"}
          className="action-button end-button"
          disabled={isEnding}
          loading={isEnding}
          onClick={onEndNetwork}
        />
        {onOpenConfig && (
          <Button
            size="small"
            icon={<SettingsIcon />}
            content={translation?.configure || "Configure"}
            className="action-button config-button"
            onClick={onOpenConfig}
          />
        )}
      </div>
    </Card>
  );
};

export default TrustedNetworkCard;
